import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/lib/auth";
import { LangProvider } from "@/lib/lang";
import { ProtectedRoute } from "@/lib/protected";

import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import EmailVerify from "@/pages/EmailVerify";
import AdminLayout from "@/components/admin/AdminLayout";
import Dashboard from "@/pages/admin/Dashboard";
import Drivers from "@/pages/admin/Drivers";
import DriverDetail from "@/pages/admin/DriverDetail";
import Vehicles from "@/pages/admin/Vehicles";
import Fleets from "@/pages/admin/Fleets";
import Partners from "@/pages/admin/Partners";
import Tours from "@/pages/admin/Tours";
import FixedRoutes from "@/pages/admin/FixedRoutes";
import Bookings from "@/pages/admin/Bookings";
import BookingDetail from "@/pages/admin/BookingDetail";
import Reviews from "@/pages/admin/Reviews";
import Financial from "@/pages/admin/Financial";
import FeeConfig from "@/pages/admin/FeeConfig";
import Documents from "@/pages/admin/Documents";
import Clients from "@/pages/admin/Clients";
import Integrations from "@/pages/admin/Integrations";
import LegalPages from "@/pages/admin/LegalPages";
import SystemSettings from "@/pages/admin/SystemSettings";
import DispatchSettings from "@/pages/admin/DispatchSettings";
import Approvals from "@/pages/admin/Approvals";
import Calendar from "@/pages/admin/Calendar";
import FleetDetail from "@/pages/admin/FleetDetail";
import PartnerDetail from "@/pages/admin/PartnerDetail";
import VehicleCategories from "@/pages/admin/VehicleCategories";
import Zones from "@/pages/admin/Zones";
import Seasons from "@/pages/admin/Seasons";
import Landing from "@/pages/site/Landing";
import OwnerDashboard from "@/pages/owner/OwnerDashboard";
import WhatsAppSettings from "@/pages/admin/WhatsAppSettings";
import BlockedDashboard from "@/pages/admin/BlockedDashboard";
import { OwnerPortalLayout } from "@/components/portal/PortalLayout";
import LegalView from "@/pages/LegalView";
import PublicPartners from "@/pages/PublicPartners";
import PartnerBookingPage from "@/pages/PartnerBookingPage";
import PartnerLayout, { FleetPortalLayout } from "@/components/portal/PortalLayout";
import ClientPortalShell from "@/pages/client/ClientPortalShell";
import PartnerDashboard from "@/pages/partner/PartnerDashboard";
import PartnerBookings from "@/pages/partner/PartnerBookings";
import PartnerMarkup from "@/pages/partner/PartnerMarkup";
import PartnerFixedRoutes from "@/pages/partner/PartnerFixedRoutes";
import PartnerEmbed from "@/pages/partner/PartnerEmbed";
import PartnerStatements from "@/pages/partner/PartnerStatements";
import PartnerCatalog from "@/pages/partner/PartnerCatalog";
import PartnerTours from "@/pages/partner/PartnerTours";
import PartnerVehicleCategories from "@/pages/partner/PartnerVehicleCategories";
import PartnerSeasons from "@/pages/partner/PartnerSeasons";
import FleetDashboard from "@/pages/fleet/FleetDashboard";
import FleetDrivers from "@/pages/fleet/FleetDrivers";
import FleetVehicles from "@/pages/fleet/FleetVehicles";
import FleetEarnings from "@/pages/fleet/FleetEarnings";
import ClientRegister from "@/pages/client/ClientRegister";
import ClientVerify from "@/pages/client/ClientVerify";
import ClientDashboard from "@/pages/client/ClientDashboard";
import ClientBookings from "@/pages/client/ClientBookings";
import ClientReferrals from "@/pages/client/ClientReferrals";
import ClientProfile from "@/pages/client/ClientProfile";

import DriverLayout from "@/components/driver/DriverLayout";
import Today from "@/pages/driver/Today";
import TripDetail from "@/pages/driver/TripDetail";
import Schedule from "@/pages/driver/Schedule";
import Earnings from "@/pages/driver/Earnings";
import DriverProfile from "@/pages/driver/DriverProfile";
import BookingTracking from "@/pages/BookingTracking";
import MagicAuth from "@/pages/MagicAuth";
import DriverInstall from "@/pages/DriverInstall";
import ClientInstall from "@/pages/ClientInstall";
import ClientSignup from "@/pages/ClientSignup";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AppUpdateListener from "@/lib/AppUpdateListener";

function App() {
  return (
    <AuthProvider>
      <LangProvider>
        <BrowserRouter>
          <AppUpdateListener />
          <Toaster theme="light" position="top-right" toastOptions={{ style: { background: "#ffffff", border: "1px solid #f59e0b", color: "#0a0f1e", borderRadius: 0 } }} />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/booking/:reference" element={<BookingTracking />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/verify" element={<EmailVerify />} />
            <Route path="/auth/magic" element={<MagicAuth />} />
            <Route path="/motorista/instalar" element={<DriverInstall />} />
            <Route path="/cliente/instalar" element={<ClientInstall />} />
            <Route path="/cliente/criar-conta" element={<ClientSignup />} />
            <Route path="/esqueci-password" element={<ForgotPassword />} />
            <Route path="/redefinir-password" element={<ResetPassword />} />
            <Route path="/app" element={<Landing />} />
            <Route path="/install" element={<DriverInstall />} />

            {/* Admin */}
            <Route path="/admin" element={<ProtectedRoute allowed={["admin"]}><AdminLayout /></ProtectedRoute>}>
              <Route index element={<Dashboard />} />
              <Route path="drivers" element={<Drivers />} />
              <Route path="drivers/:id" element={<DriverDetail />} />
              <Route path="vehicles" element={<Vehicles />} />
              <Route path="fleets" element={<Fleets />} />
              <Route path="fleets/:id" element={<FleetDetail />} />
              <Route path="partners" element={<Partners />} />
              <Route path="partners/:id" element={<PartnerDetail />} />
              <Route path="tours" element={<Tours />} />
              <Route path="routes" element={<FixedRoutes />} />
              <Route path="bookings" element={<Bookings />} />
              <Route path="bookings/:id" element={<BookingDetail />} />
              <Route path="reviews" element={<Reviews />} />
              <Route path="finance" element={<Financial />} />
              <Route path="fees" element={<FeeConfig />} />
              <Route path="documents" element={<Documents />} />
              <Route path="clients" element={<Clients />} />
              <Route path="approvals" element={<Approvals />} />
              <Route path="calendar" element={<Calendar />} />
              <Route path="vehicle-categories" element={<VehicleCategories />} />
              <Route path="zones" element={<Zones />} />
              <Route path="seasons" element={<Seasons />} />
              <Route path="settings/integrations" element={<Integrations />} />
              <Route path="settings/legal" element={<LegalPages />} />
              <Route path="settings/system" element={<SystemSettings />} />
              <Route path="settings/dispatch" element={<DispatchSettings />} />
              <Route path="settings/whatsapp" element={<WhatsAppSettings />} />
              <Route path="blocked" element={<BlockedDashboard />} />
            </Route>

            {/* Driver */}
            <Route path="/driver" element={<ProtectedRoute allowed={["driver"]}><DriverLayout /></ProtectedRoute>}>
              <Route index element={<Today />} />
              <Route path="trip/:id" element={<TripDetail />} />
              <Route path="schedule" element={<Schedule />} />
              <Route path="earnings" element={<Earnings />} />
              <Route path="profile" element={<DriverProfile />} />
            </Route>

            {/* Public legal pages */}
            <Route path="/legal/:pagePath" element={<LegalView />} />
            <Route path="/termos" element={<LegalView />} />
            <Route path="/privacidade" element={<LegalView />} />
            <Route path="/cookies" element={<LegalView />} />
            <Route path="/reclamacoes" element={<LegalView />} />
            <Route path="/partners" element={<PublicPartners />} />
            <Route path="/book/:slug" element={<PartnerBookingPage />} />

            {/* Public client registration */}
            <Route path="/register" element={<ClientRegister />} />
            <Route path="/client/verify" element={<ClientVerify />} />

            {/* Client portal */}
            <Route path="/client-portal" element={<ProtectedRoute allowed={["client"]}><ClientPortalShell /></ProtectedRoute>}>
              <Route index element={<ClientDashboard />} />
              <Route path="bookings" element={<ClientBookings />} />
              <Route path="referrals" element={<ClientReferrals />} />
              <Route path="profile" element={<ClientProfile />} />
            </Route>

            {/* Partner portal */}
            <Route path="/partner-portal" element={<ProtectedRoute allowed={["partner"]}><PartnerLayout /></ProtectedRoute>}>
              <Route index element={<PartnerDashboard />} />
              <Route path="tours" element={<PartnerTours />} />
              <Route path="vehicle-categories" element={<PartnerVehicleCategories />} />
              <Route path="seasons" element={<PartnerSeasons />} />
              <Route path="catalog" element={<PartnerCatalog />} />
              <Route path="bookings" element={<PartnerBookings />} />
              <Route path="markup" element={<PartnerMarkup />} />
              <Route path="fixed-routes" element={<PartnerFixedRoutes />} />
              <Route path="embed" element={<PartnerEmbed />} />
              <Route path="statements" element={<PartnerStatements />} />
            </Route>

            {/* Fleet portal */}
            <Route path="/fleet-portal" element={<ProtectedRoute allowed={["fleet_owner"]}><FleetPortalLayout /></ProtectedRoute>}>
              <Route index element={<FleetDashboard />} />
              <Route path="drivers" element={<FleetDrivers />} />
              <Route path="vehicles" element={<FleetVehicles />} />
              <Route path="earnings" element={<FleetEarnings />} />
            </Route>

            {/* Vehicle Owner portal — deprecated iter 23 (Msg 679). Fundido com Fleet. Rota mantida temporariamente para dados legados. */}
            <Route path="/owner-portal" element={<ProtectedRoute allowed={["vehicle_owner"]}><OwnerPortalLayout /></ProtectedRoute>}>
              <Route index element={<OwnerDashboard />} />
            </Route>

            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </BrowserRouter>
      </LangProvider>
    </AuthProvider>
  );
}

export default App;
